/**
 * Editor de destacada (oct-2026, founder) — una destacada es una COLECCIÓN de historias (como Instagram).
 *  · create: se abre al terminar de subir una historia con el flujo normal («+ Nuevo»): nombre (≤15) + portada;
 *    permite agregar varias seguidas (subir otra / desde mi archivo) antes de guardar.
 *  · edit: nombre, portada, quitar (no borra la historia), reordenar (↑/↓), agregar más y eliminar la destacada.
 * Nunca sube archivos: solo guarda IDs de historias ya publicadas.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Upload, Archive, Trash2, ArrowUp, ArrowDown, Loader2, X, Play, Star } from 'lucide-react';
import { toast } from 'sonner';
import { highlightsApi } from '@/lib/highlightsApi';
import { friendlyError } from '@/lib/apiError';
import { StoryArchiveSheet } from './StoryArchiveSheet';
import { HighlightTitleInput } from './HighlightPickerSheet';
import { tr, localeTag } from '@/contexts/LanguageContext';

const MAX_ITEMS = 50;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(localeTag(), { day: 'numeric', month: 'short' }) : '');
const thumbOf = (s) => (s.kind === 'image' ? s.url : s.thumbnail_url);

export const HighlightEditor = ({ open, onOpenChange, mode = 'create', highlight, initialStories = [], uploading = 0, hidden = false, onUploadNew, onSaved, onDeleted }) => {
  const [stories, setStories] = useState([]);
  const [title, setTitle] = useState('');
  const [cover, setCover] = useState(null);
  const [busy, setBusy] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBusy(false); setArchiveOpen(false); setConfirmDelete(false);
    setStories(highlight?.stories ? highlight.stories.map((s) => ({ ...s })) : []);
    setTitle(highlight?.title || '');
    setCover(highlight?.cover_url || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, highlight?.id]);

  // create: las historias que van terminando de subirse llegan por `initialStories` (se agregan sin duplicar).
  useEffect(() => {
    if (!open || !initialStories?.length) return;
    setStories((prev) => {
      const have = new Set(prev.map((s) => s.id));
      const add = initialStories.filter((s) => !have.has(s.id));
      return add.length ? [...prev, ...add] : prev;
    });
  }, [open, initialStories]);

  const coverOptions = useMemo(() => stories.map(thumbOf).filter(Boolean), [stories]);
  const effectiveCover = cover && coverOptions.includes(cover) ? cover : coverOptions[0] || null;
  const move = (i, d) => setStories((prev) => { const a = [...prev]; const j = i + d; if (j < 0 || j >= a.length) return prev; [a[i], a[j]] = [a[j], a[i]]; return a; });
  const removeAt = (i) => setStories((prev) => prev.filter((_, j) => j !== i));
  const addFromArchive = async (ids) => {
    const r = await highlightsApi.myStories('all');
    const by = new Map(r.items.map((s) => [s.id, s]));
    setStories((prev) => {
      const have = new Set(prev.map((s) => s.id));
      const add = ids.filter((id) => !have.has(id) && by.has(id)).map((id) => by.get(id));
      if (prev.length + add.length > MAX_ITEMS) { toast.error(tr("Máximo {0} historias por destacada", MAX_ITEMS)); return prev; }
      return [...prev, ...add];
    });
  };

  const save = async () => {
    if (!title.trim()) { toast.error(tr("Ponle un nombre a la destacada (máx. 15 caracteres)")); return; }
    if (!stories.length) { toast.error(tr("Agrega al menos una historia")); return; }
    setBusy(true);
    try {
      const body = { title: title.trim(), coverUrl: effectiveCover, storyIds: stories.map((s) => s.id) };
      const r = mode === 'edit' && highlight?.id ? await highlightsApi.update(highlight.id, body) : await highlightsApi.create(body);
      toast.success(mode === 'edit' ? tr("Destacada actualizada") : tr("Destacada «{0}» publicada", r.highlight.title));
      onSaved?.(r.highlight);
    } catch (e) { toast.error(friendlyError(e, tr("No pudimos guardar la destacada"))); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await highlightsApi.remove(highlight.id); toast.success(tr("Destacada eliminada. Tus historias siguen en tu archivo.")); onDeleted?.(highlight); }
    catch (e) { toast.error(friendlyError(e, tr("No pudimos eliminar la destacada"))); } finally { setBusy(false); }
  };

  if (!open) return null;
  return createPortal(
    <>
    <div className={`fixed inset-0 z-[200] bg-background/80 backdrop-blur-sm flex items-end sm:items-center justify-center ${hidden ? 'hidden' : ''}`} onClick={() => !busy && onOpenChange?.(false)} data-testid="highlight-editor" data-hidden={hidden ? 'true' : undefined}>
      <div className="w-full sm:max-w-lg max-h-[94vh] rounded-t-3xl sm:rounded-3xl border border-border bg-card flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h3 className="font-display text-base font-bold flex items-center gap-2"><Star className="w-4 h-4 text-primary" />{mode === 'edit' ? tr("Editar destacada") : tr("Nueva destacada")}</h3>
          <button type="button" onClick={() => onOpenChange?.(false)} className="w-8 h-8 rounded-full hover:bg-secondary flex items-center justify-center" aria-label={tr("Cerrar")} data-testid="highlight-editor-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-3 space-y-4">
          {mode === 'create' && <p className="text-[11px] text-muted-foreground">{tr("Tu historia ya se publicó (24 h en tu riel). Aquí queda guardada para siempre dentro de la destacada.")}</p>}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{tr("Nombre")}</label>
            <HighlightTitleInput value={title} onChange={setTitle} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{tr("Portada")}</label>
            <div className="flex gap-2 overflow-x-auto py-2" data-testid="highlight-cover-options">
              {coverOptions.length === 0 && <p className="text-xs text-muted-foreground">{tr("La portada se toma de tus historias.")}</p>}
              {coverOptions.map((u) => (
                <button type="button" key={u} onClick={() => setCover(u)} className={`w-16 h-16 flex-shrink-0 rounded-full overflow-hidden border-[3px] ${effectiveCover === u ? 'border-primary' : 'border-transparent'}`} data-testid="highlight-cover-option">
                  <img src={u} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{tr("Historias ({0})", stories.length)}</label>
              <span className="text-[10px] text-muted-foreground">{tr("Quitar no borra la historia")}</span>
            </div>
            <ul className="space-y-1.5" data-testid="highlight-stories-list">
              {Array.from({ length: uploading }).map((_, i) => (
                <li key={`up-${i}`} className="flex items-center gap-2 rounded-xl border border-dashed border-primary/40 bg-primary/5 p-2 text-xs text-primary" data-testid="highlight-uploading-row">
                  <Loader2 className="w-4 h-4 animate-spin" />{tr("Subiendo tu historia… puedes ponerle nombre mientras tanto.")}
                </li>
              ))}
              {stories.map((s, i) => (
                <li key={s.id} className="flex items-center gap-2 bg-background border border-border rounded-xl p-2" data-testid={`highlight-item-${i}`}>
                  <span className="relative w-12 h-16 rounded-lg overflow-hidden bg-muted shrink-0">
                    {thumbOf(s) ? <img src={thumbOf(s)} alt="" className="w-full h-full object-cover" /> : <span className="w-full h-full flex items-center justify-center bg-black/60"><Play className="w-4 h-4 text-white/80" /></span>}
                    {s.kind === 'video' && <Play className="absolute top-1 left-1 w-3 h-3 text-white drop-shadow" />}
                  </span>
                  <span className="flex-1 min-w-0 text-xs text-muted-foreground">
                    <span className="block text-foreground font-semibold">{s.kind === 'video' ? tr("Video · {0} s", Math.round(s.duration_seconds || 15)) : tr("Foto")}</span>
                    <span className="block">{fmt(s.created_at)}{s.expired ? ` · ${tr("archivo")}` : ''}</span>
                  </span>
                  <button type="button" onClick={() => move(i, -1)} className="p-1.5 rounded-lg hover:bg-secondary disabled:opacity-30" disabled={i === 0} aria-label={tr("Subir")} data-testid={`highlight-item-${i}-up`}><ArrowUp className="w-4 h-4" /></button>
                  <button type="button" onClick={() => move(i, 1)} className="p-1.5 rounded-lg hover:bg-secondary disabled:opacity-30" disabled={i === stories.length - 1} aria-label={tr("Bajar")} data-testid={`highlight-item-${i}-down`}><ArrowDown className="w-4 h-4" /></button>
                  <button type="button" onClick={() => removeAt(i)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive" aria-label={tr("Quitar")} data-testid={`highlight-item-${i}-remove`}><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
              {!stories.length && !uploading && <li className="text-xs text-muted-foreground text-center py-4" data-testid="highlight-stories-empty">{tr("Aún no hay historias en esta destacada.")}</li>}
            </ul>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button type="button" onClick={() => onUploadNew?.()} disabled={!onUploadNew || busy} className="rounded-xl border border-primary/40 bg-background p-3 text-left hover:bg-primary/5 disabled:opacity-50" data-testid="highlight-add-upload">
                <Upload className="w-4 h-4 text-primary mb-1" /><span className="block text-xs font-semibold">{tr("Subir historia nueva")}</span><span className="block text-[10px] text-muted-foreground">{tr("Se publica 24 h y queda aquí")}</span>
              </button>
              <button type="button" onClick={() => setArchiveOpen(true)} disabled={busy} className="rounded-xl border border-border bg-background p-3 text-left hover:bg-primary/5 disabled:opacity-50" data-testid="highlight-add-archive">
                <Archive className="w-4 h-4 text-primary mb-1" /><span className="block text-xs font-semibold">{tr("Desde mi archivo")}</span><span className="block text-[10px] text-muted-foreground">{tr("Activas y caducadas")}</span>
              </button>
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-border space-y-2">
          <Button className="w-full h-11 rounded-full" disabled={busy || !title.trim() || !stories.length} onClick={save} data-testid="highlight-publish-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'edit' ? tr("Guardar cambios") : tr("Guardar destacada"))}
          </Button>
          {mode === 'edit' && !confirmDelete && (
            <button type="button" onClick={() => setConfirmDelete(true)} className="w-full text-xs font-semibold text-destructive py-1" data-testid="highlight-delete-btn">{tr("Eliminar destacada")}</button>
          )}
          {mode === 'edit' && confirmDelete && (
            <div className="flex items-center gap-2" data-testid="highlight-delete-confirm">
              <p className="flex-1 text-[11px] text-muted-foreground">{tr("¿Eliminar «{0}»? Tus historias no se borran.", highlight?.title)}</p>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => setConfirmDelete(false)}>{tr("No")}</Button>
              <Button variant="destructive" size="sm" className="rounded-full" disabled={busy} onClick={remove} data-testid="highlight-delete-confirm-btn">{tr("Sí, eliminar")}</Button>
            </div>
          )}
        </div>
      </div>
    </div>
    <StoryArchiveSheet open={archiveOpen} onOpenChange={setArchiveOpen} excludeIds={stories.map((s) => s.id)} onConfirm={addFromArchive} confirmLabel={tr("Agregar")} />
    </>, document.body);
};

export default HighlightEditor;
